import {
  Control,
  FieldErrors,
  useFormContext,
  useFormState,
} from 'react-hook-form';
import { InfoCircleOutlined } from '@ant-design/icons';
import {
  DataCaptureEditFormData,
  DataCaptureEditFormInputProps,
  ProcessFieldTypes,
} from 'types/dataCaptureTypes';
import { InputType } from 'types/genericTypes';
import { GenericInput } from '../GenericInput';
import { TransformedField } from './utils';

type EditDataCaptureFormSectionFieldProps = {
  field: TransformedField;
  fieldIndex: number;
  sectionIndex: number;
  control: Control<DataCaptureEditFormData>;
  errors: FieldErrors<DataCaptureEditFormData>;
  isViewForm: boolean;
  defaultText?: string;
  defaultTooltipText?: string;
  isVisible?: boolean;
};

export const EditDataCaptureFormSectionField = ({
  field,
  fieldIndex,
  sectionIndex,
  control,
  errors,
  isViewForm,
  defaultText = '',
  defaultTooltipText = '',
  isVisible = true,
}: EditDataCaptureFormSectionFieldProps) => {
  const { watch } = useFormContext();
  const { dirtyFields } = useFormState();

  // label translation logic
  const fieldName = `sections.${sectionIndex}.fields.${fieldIndex}.field_name`;

  const currentValue = watch(fieldName);

  const getNestedValue = (obj: any, path: string): any => {
    if (!obj) return undefined;

    return path.split('.').reduce((acc: any, part: string) => {
      if (acc === null || acc === undefined) return undefined;

      if (Array.isArray(acc)) {
        const index = Number(part);
        return acc[index];
      }

      return acc[part];
    }, obj);
  };

  const wasPreviouslyEdited =
    field.field_name && field.field_name !== field.original_field_name;

  const isDirty = !!getNestedValue(dirtyFields, fieldName);

  // final logic to display text
  const displayText = isDirty
    ? currentValue
    : wasPreviouslyEdited
    ? field.field_name
    : defaultText;

  // tooltip translation logic
  const tooltipFieldName = `sections.${sectionIndex}.fields.${fieldIndex}.help_text`;

  const currentTooltipValue = watch(tooltipFieldName);

  const isTooltipDirty = !!getNestedValue(dirtyFields, tooltipFieldName);

  const wasTooltipPreviouslyEdited =
    field.help_text && field.help_text !== field.original_help_text;

  const displayTooltipText = isTooltipDirty
    ? currentTooltipValue
    : wasTooltipPreviouslyEdited
    ? field.help_text
    : defaultTooltipText;

  const getInputType = (fieldType: string) => {
    if (fieldType === ProcessFieldTypes.Text || fieldType === 'date')
      return InputType.INPUT;
    if (fieldType === ProcessFieldTypes.Select) return InputType.DROPDOWN;
    if (fieldType === ProcessFieldTypes.Radio) return InputType.SWITCH;
    return InputType.INPUT;
  };

  const inputs: DataCaptureEditFormInputProps = [
    {
      inputType: getInputType(field.field_type),
      fieldName: `sections.${sectionIndex}.fields.${fieldIndex}.field_value`,
      label: displayText,
      showRequiredAsterisk: field.is_required,
      inputRules: {},
      disabled: true,
      tooltipText: displayTooltipText,
      tooltipIcon: <InfoCircleOutlined />,
      displayDataCaptureEditButton: !isViewForm,
      originalFieldName: field.original_field_name,
      originalHelpText: field.original_help_text,
      defaultFieldName: defaultText,
      defaultHelpText: defaultTooltipText,
      isVisible: field.is_visible,
    },
  ];

  return (
    <>
      {inputs.map(input => (
        <GenericInput<DataCaptureEditFormData>
          key={input.fieldName}
          control={control}
          errors={errors}
          {...input}
        />
      ))}
    </>
  );
};
